import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Menu, Search, ShoppingBag, Phone, X, ChevronDown, User, LogOut, Package, Truck,
  Heart, MessageCircleHeart, Cake, PartyPopper, Baby,
  Flower2, Gem, HandHeart, Sparkles, HeartPulse, Diamond, HeartCrack,
} from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "./ui/sheet";
import { navigation, emotions } from "../data/siteData";
import { useCart } from "../lib/CartContext";
import { useProducts } from "../lib/ProductsContext";
import { useZeoContent } from "../lib/ZeoContentContext";
import { useCustomerAuth } from "../lib/CustomerAuthContext";
import { useScrollThreshold } from "../hooks/use-scroll-threshold";
import AuthModal from "./AuthModal";

const emotionIcons = {
  Heart, MessageCircleHeart, Cake, PartyPopper, Baby,
  Flower2, Gem, HandHeart, Sparkles, HeartPulse, Diamond, HeartCrack,
};

// RoseBox'ta her ana kategori (Çiçek, Güller, Orkide, Saksı Çiçekler...) kendi
// üst menü öğesi ve üstüne gelince açılan kendi alt kategori paneli var — tek
// bir "Kategoriler" şemsiyesi altında değil. Aynı deseni panelden gelen GERÇEK
// ana kategorilerle kuruyoruz.
//
// GÜNCELLEME: navigasyonda hangi 5 ana kategorinin, hangi SIRAYLA görüneceği
// artık panelin sort_order'ından BAĞIMSIZ, burada sabitlenmiş bir listeyle
// belirleniyor — kullanıcının istediği kurumsal, tutarlı sıralama (canlıdaki
// kategori isimleriyle BİREBİR eşleştirilerek doğrulandı). İsim eşleşmesi
// büyük/küçük harfe duyarsız; panelde bu isimlerden biri yeniden adlandırılır/
// silinirse o öğe sessizce atlanır — kırık bir bağlantı asla gösterilmez.
// Bu 5'in dışındaki TÜM kategoriler (kaç tane olursa olsun) "Diğer
// Kategoriler" açılır menüsünde toplanır — nav hiçbir zaman taşıp ikinci
// satıra düşmez.
const PINNED_CATEGORY_ORDER = ["Çiçek", "Güller", "Orkide", "Premium Çiçek", "Saksı Çiçekler"];

export default function Header() {
  const navigate = useNavigate();
  const { cart } = useCart();
  const { business } = useZeoContent();
  const { categories } = useProducts();
  const { customer, isLoggedIn, logout } = useCustomerAuth();
  const cartCount = cart.reduce((s, i) => s + i.qty, 0);
  const scrolled = useScrollThreshold(20);
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [openMenu, setOpenMenu] = useState(null);
  const [accountHover, setAccountHover] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null);

  const topCategories = categories.filter((c) => !c.parentId);
  const pinnedNamesLower = PINNED_CATEGORY_ORDER.map((n) => n.toLowerCase());
  const visibleTopCategories = PINNED_CATEGORY_ORDER
    .map((name) => topCategories.find((c) => c.name.toLowerCase() === name.toLowerCase()))
    .filter(Boolean);
  const overflowTopCategories = topCategories.filter(
    (c) => !pinnedNamesLower.includes(c.name.toLowerCase())
  );
  // Mobil menü tüm kategorileri gösterir (hiçbiri gizlenmez) — yalnızca
  // sıralaması masaüstüyle tutarlı olsun diye önce sabitlenmiş 5'i, sonra
  // kalanları listeler.
  const mobileTopCategories = [...visibleTopCategories, ...overflowTopCategories];
  const childrenOf = (catId) => categories.filter((c) => c.parentId === catId);

  const closeAll = () => { setOpen(false); setOpenMenu(null); };

  return (
    <>
      {/* Top announcement bar */}
      <div className="hidden md:flex bg-[#222222] text-white/90 text-xs tracking-[0.18em] uppercase justify-center items-center py-2.5 px-6 gap-8" data-testid="top-bar">
        <span>✓ Aynı Gün Teslimat</span>
        <span className="opacity-30">|</span>
        <span>✓ Taze Çiçek Garantisi</span>
        <span className="opacity-30">|</span>
        <span>✓ 7/24 Sipariş Hattı: {business.phone}</span>
        <span className="opacity-30">|</span>
        <span>★ 5.0 / 150+ Yorum</span>
      </div>

      <header
        className={`sticky top-0 z-50 w-full transition-all duration-300 ${
          scrolled
            ? "bg-white/85 backdrop-blur-xl border-b border-[#EAEAEA] shadow-[0_4px_30px_rgba(0,0,0,0.04)]"
            : "bg-white border-b border-transparent"
        }`}
        data-testid="site-header"
      >
        <div className="frida-container">
          {/* GÜNCELLEME: eski "1fr auto 1fr" grid yaklaşımı navigasyonu
              matematiksel olarak yaklaşık ortalıyordu ama logo (258px) ile
              ikon kümesi (184px) FARKLI genişlikte olduğundan, iki dış
              sütunun kendi İÇİNDEKİ boşluklar eşit değildi — nav bloğunun
              etrafındaki boşluk görsel olarak asimetrik kalıyordu (kullanıcı
              geri bildirimi: hâlâ tam ortalı görünmüyordu). Kesin, ölçüme
              dayalı çözüm: logo/ikonlar normal flex akışında solda/sağda
              kalır (justify-between), navigasyon ise `position: absolute` +
              `left-1/2 -translate-x-1/2` ile bu satırın TAM genişliğine göre
              ortalanır — logo/ikon genişlik farkından tamamen bağımsız,
              matematiksel olarak kesin merkez.
              Kırılma noktası kasıtlı olarak `lg` (1024px) değil `xl`
              (1280px) — tüm navigasyon öğeleri + logo + ikonlar 1024-1279px
              aralığında fiziksel olarak sığmıyor; bu aralıkta temiz açılır
              menüye düşülüyor. 1280px'in TAM kendisinde bile ortalanmış
              navigasyonun logoyla ÇAKIŞMAMASI için nav öğelerinin dolgusu
              sıkılaştırıldı ve "Diğer Kategoriler" "Diğer"e kısaltıldı —
              ölçülüp doğrulandı (bkz. devam notu). */}
          <div className="relative flex items-center justify-between gap-4 h-20 lg:h-24">
            {/* Logo — her zaman sol kenara yapışık, konteynerin kendi
                dolgusu (frida-container padding) sayesinde tarayıcı
                kenarından hafif boşluklu. */}
            <Link to="/" className="flex items-center gap-1.5 sm:gap-3 group min-w-0 flex-1 xl:flex-none" data-testid="logo-link">
              <img
                src="/frida-cicekcilik-logo.png"
                alt="Frida Çiçekçilik"
                width="80"
                height="80"
                className="w-14 h-14 sm:w-20 sm:h-20 object-contain shrink-0 group-hover:scale-105 transition-transform"
              />
              <div className="min-w-0">
                <div className="font-serif text-base sm:text-xl lg:text-2xl font-bold tracking-tight leading-none truncate">
                  Frida Çiçekçilik
                </div>
                {/* Etiket satırı yalnızca xl altında (kompakt masaüstü çubuğunda
                    değil) gösterilir — logonun genişliği ikon kümesiyle daha
                    dengeli kalsın diye, navigasyonun gerçekten ortalanabilmesi
                    için (bkz. yukarıdaki grid yorumu). Tam metin Hero ve
                    Footer'da zaten görünür durumda. */}
                <div className="xl:hidden text-[8px] sm:text-[10px] leading-tight tracking-[0.04em] sm:tracking-[0.25em] uppercase text-[#888888] mt-0.5 sm:mt-1">
                  Çiçekle Duygularınızı Anlatın
                </div>
              </div>
            </Link>

            {/* Desktop Nav — RoseBox tarzı: her ana kategori kendi üstüne
                gelince açılan alt kategori panelini gösterir. Alt kategorisi
                olmayan bir kategori sade bir bağlantı olarak kalır (boş/awkward
                bir panel göstermek yerine — "sığma" durumunda profesyonel yol). */}
            <nav
              className="hidden xl:flex items-center absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
              data-testid="desktop-nav"
            >
              {navigation.before.map((item) => (
                <Link
                  key={item.label}
                  to={item.to}
                  className="px-1.5 py-2 text-sm font-medium text-[#222222] hover:text-[#A85C34] transition-colors link-underline whitespace-nowrap"
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {item.label}
                </Link>
              ))}

              {visibleTopCategories.map((cat) => {
                const children = childrenOf(cat.id);
                const menuKey = `cat-${cat.id}`;
                const isOpen = openMenu === menuKey;

                if (children.length === 0) {
                  return (
                    <Link
                      key={cat.id}
                      to={`/kategori/${cat.id}`}
                      className="px-1.5 py-2 text-sm font-medium text-[#222222] hover:text-[#A85C34] transition-colors link-underline whitespace-nowrap"
                      data-testid={`nav-category-${cat.id}`}
                    >
                      {cat.name}
                    </Link>
                  );
                }

                return (
                  <div
                    key={cat.id}
                    className="relative"
                    onMouseEnter={() => setOpenMenu(menuKey)}
                    onMouseLeave={() => setOpenMenu((cur) => (cur === menuKey ? null : cur))}
                    onFocus={() => setOpenMenu(menuKey)}
                    onBlur={(e) => {
                      if (!e.currentTarget.contains(e.relatedTarget)) {
                        setOpenMenu((cur) => (cur === menuKey ? null : cur));
                      }
                    }}
                  >
                    <Link
                      to={`/kategori/${cat.id}`}
                      className="px-1.5 py-2 text-sm font-medium text-[#222222] hover:text-[#A85C34] transition-colors link-underline whitespace-nowrap inline-flex items-center gap-0.5"
                      data-testid={`nav-category-${cat.id}`}
                    >
                      {cat.name}
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                    </Link>
                    {isOpen && (
                      <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 z-50" data-testid={`category-dropdown-${cat.id}`}>
                        <div className={`bg-white rounded-2xl border border-[#EAEAEA] shadow-[0_20px_60px_rgba(0,0,0,0.12)] p-4 max-w-[90vw] ${children.length > 6 ? "w-[440px] grid grid-cols-2 gap-1" : "w-[260px] flex flex-col gap-0.5"}`}>
                          <Link
                            to={`/kategori/${cat.id}`}
                            className="px-3 py-2.5 rounded-xl text-sm font-semibold text-[#A85C34] hover:bg-[#FBF9F4] transition-colors truncate col-span-full"
                          >
                            Tümünü Gör
                          </Link>
                          {children.map((child) => (
                            <Link
                              key={child.id}
                              to={`/kategori/${child.id}`}
                              className="px-3 py-2.5 rounded-xl text-sm text-[#222222] hover:bg-[#FBF9F4] hover:text-[#A85C34] transition-colors truncate"
                              data-testid={`category-dropdown-${cat.id}-${child.id}`}
                            >
                              {child.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {overflowTopCategories.length > 0 && (
                <div
                  className="relative"
                  onMouseEnter={() => setOpenMenu("overflow")}
                  onMouseLeave={() => setOpenMenu((cur) => (cur === "overflow" ? null : cur))}
                  onFocus={() => setOpenMenu("overflow")}
                  onBlur={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget)) {
                      setOpenMenu((cur) => (cur === "overflow" ? null : cur));
                    }
                  }}
                >
                  <button
                    type="button"
                    className="px-1.5 py-2 text-sm font-medium text-[#222222] hover:text-[#A85C34] transition-colors whitespace-nowrap inline-flex items-center gap-0.5"
                    data-testid="nav-diger-kategoriler"
                    aria-label="Diğer Kategoriler"
                  >
                    Diğer
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${openMenu === "overflow" ? "rotate-180" : ""}`} />
                  </button>
                  {openMenu === "overflow" && (
                    <div className="absolute top-full right-0 pt-2 z-50" data-testid="overflow-dropdown">
                      <div className="w-64 max-w-[90vw] bg-white rounded-2xl border border-[#EAEAEA] shadow-[0_20px_60px_rgba(0,0,0,0.12)] p-2 flex flex-col gap-0.5">
                        {overflowTopCategories.map((cat) => (
                          <Link
                            key={cat.id}
                            to={`/kategori/${cat.id}`}
                            className="px-3 py-2.5 rounded-xl text-sm text-[#222222] hover:bg-[#FBF9F4] hover:text-[#A85C34] transition-colors truncate"
                          >
                            {cat.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {navigation.after.map((item) => {
                if (!item.type) {
                  return (
                    <Link
                      key={item.label}
                      to={item.to}
                      className="px-1.5 py-2 text-sm font-medium text-[#222222] hover:text-[#A85C34] transition-colors link-underline whitespace-nowrap"
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {item.label}
                    </Link>
                  );
                }

                const isOpen = openMenu === item.label;
                return (
                  <div
                    key={item.label}
                    className="relative"
                    onMouseEnter={() => setOpenMenu(item.label)}
                    onMouseLeave={() => setOpenMenu((cur) => (cur === item.label ? null : cur))}
                    onFocus={() => setOpenMenu(item.label)}
                    onBlur={(e) => {
                      if (!e.currentTarget.contains(e.relatedTarget)) {
                        setOpenMenu((cur) => (cur === item.label ? null : cur));
                      }
                    }}
                  >
                    <Link
                      to={item.to}
                      className="px-1.5 py-2 text-sm font-medium text-[#222222] hover:text-[#A85C34] transition-colors link-underline whitespace-nowrap inline-flex items-center gap-0.5"
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {item.label}
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                    </Link>
                    {isOpen && (
                      <div className="absolute top-full right-0 pt-2 z-50" data-testid="occasions-dropdown">
                        <div className="w-[600px] max-w-[90vw] bg-white rounded-2xl border border-[#EAEAEA] shadow-[0_20px_60px_rgba(0,0,0,0.12)] p-5">
                          <p className="text-[10px] uppercase tracking-[0.18em] text-[#888888] font-medium mb-3 px-1">
                            Vesileye Göre Seçin
                          </p>
                          <div className="grid grid-cols-3 gap-1">
                            {emotions.map((e) => {
                              const Icon = emotionIcons[e.icon];
                              return (
                                <Link
                                  key={e.id}
                                  to={`/urunler?vesile=${e.id}`}
                                  className="group flex items-center gap-2.5 px-2.5 py-2.5 rounded-xl text-sm text-[#222222] hover:bg-[#FBF9F4] hover:text-[#A85C34] transition-colors"
                                  data-testid={`occasions-dropdown-${e.id}`}
                                >
                                  <span className="w-8 h-8 rounded-full bg-[#F5E9DD] flex items-center justify-center shrink-0 group-hover:bg-[#A85C34] transition-colors">
                                    {Icon && <Icon className="w-4 h-4 text-[#A85C34] group-hover:text-white transition-colors" />}
                                  </span>
                                  <span className="truncate">{e.label}</span>
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>

            {/* Right icons */}
            <div className="flex items-center gap-0.5 sm:gap-2 shrink-0">
              <button
                onClick={() => setSearchOpen(true)}
                aria-label="Arama"
                data-testid="search-button"
                className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center hover:bg-[#F8F8F8] transition-colors shrink-0"
              >
                <Search className="w-[18px] h-[18px] sm:w-5 sm:h-5 text-[#222222]" />
              </button>
              <Link
                to="/siparis-takibi"
                aria-label="Sipariş Takibi"
                data-testid="order-tracking-button"
                className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center hover:bg-[#F8F8F8] transition-colors shrink-0"
              >
                <Truck className="w-[18px] h-[18px] sm:w-5 sm:h-5 text-[#222222]" />
              </Link>

              {/* Hesabım */}
              <div
                className="relative hidden sm:block"
                onMouseEnter={() => setAccountHover(true)}
                onMouseLeave={() => setAccountHover(false)}
                onFocus={() => setAccountHover(true)}
                onBlur={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget)) setAccountHover(false);
                }}
              >
                <button
                  onClick={() => !isLoggedIn && setAuthOpen(true)}
                  aria-label="Hesabım"
                  aria-haspopup="true"
                  aria-expanded={accountHover}
                  data-testid="account-button"
                  className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-[#F8F8F8] transition-colors"
                >
                  <User className="w-5 h-5 text-[#222222]" />
                </button>
                {accountHover && (
                  <div className="absolute top-full right-0 pt-2 z-50" data-testid="account-dropdown">
                    <div className="w-56 bg-white rounded-2xl border border-[#EAEAEA] shadow-[0_20px_60px_rgba(0,0,0,0.12)] p-2">
                      {isLoggedIn ? (
                        <>
                          <div className="px-3 py-2.5 text-sm">
                            <p className="text-[#888888] text-xs">Merhaba,</p>
                            <p className="text-[#222222] font-medium truncate">{customer.name}</p>
                          </div>
                          <Link
                            to="/hesabim/siparislerim"
                            className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm text-[#222222] hover:bg-[#F8F8F8] transition-colors"
                            data-testid="account-orders-link"
                          >
                            <Package className="w-4 h-4 text-[#A85C34]" />
                            Siparişlerim
                          </Link>
                          <button
                            onClick={logout}
                            className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm text-[#888888] hover:bg-[#F8F8F8] hover:text-[#222222] transition-colors"
                            data-testid="account-logout"
                          >
                            <LogOut className="w-4 h-4" />
                            Çıkış Yap
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => setAuthOpen(true)}
                          className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm text-[#222222] hover:bg-[#F8F8F8] transition-colors"
                          data-testid="account-login-link"
                        >
                          <User className="w-4 h-4 text-[#A85C34]" />
                          Giriş Yap / Kayıt Ol
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <Link
                to="/sepet"
                aria-label="Sepet"
                data-testid="cart-button"
                className="relative w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center hover:bg-[#F8F8F8] transition-colors shrink-0"
              >
                <ShoppingBag className="w-[18px] h-[18px] sm:w-5 sm:h-5 text-[#222222]" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#D4A15C] text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: "var(--frida-accent)" }}>
                    {cartCount}
                  </span>
                )}
              </Link>

              {/* Mobile menu — masaüstündeki aynı hiyerarşiyi (ana kategori →
                  alt kategori) katlanır/açılır satırlarla gösterir. */}
              <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild>
                  <button
                    className="xl:hidden w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center hover:bg-[#F8F8F8] transition-colors shrink-0"
                    aria-label="Menü"
                    data-testid="mobile-menu-button"
                  >
                    <Menu className="w-[18px] h-[18px] sm:w-5 sm:h-5 text-[#222222]" />
                  </button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[88%] sm:w-[400px] p-0 bg-white">
                  <SheetTitle className="sr-only">Navigasyon Menüsü</SheetTitle>
                  <div className="flex flex-col h-full">
                    <div className="p-6 border-b border-[#EAEAEA] flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <img src="/frida-cicekcilik-logo.png" alt="Frida Çiçekçilik" width="56" height="56" className="w-14 h-14 object-contain" />
                        <div>
                          <div className="font-serif text-lg font-bold">Frida Çiçekçilik</div>
                          <div className="text-[10px] tracking-[0.2em] uppercase text-[#888888]">
                            Çiçekçilik
                          </div>
                        </div>
                      </div>
                    </div>
                    <nav className="flex-1 overflow-y-auto p-4">
                      {navigation.before.map((item) => (
                        <Link
                          key={item.label}
                          to={item.to}
                          onClick={closeAll}
                          className="block px-4 py-4 text-base font-medium text-[#222222] hover:bg-[#F8F8F8] hover:text-[#A85C34] rounded-2xl transition-colors"
                          data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          {item.label}
                        </Link>
                      ))}

                      {mobileTopCategories.map((cat) => {
                        const children = childrenOf(cat.id);
                        const menuKey = `cat-${cat.id}`;
                        const isExpanded = mobileExpanded === menuKey;

                        if (children.length === 0) {
                          return (
                            <Link
                              key={cat.id}
                              to={`/kategori/${cat.id}`}
                              onClick={closeAll}
                              className="block px-4 py-4 text-base font-medium text-[#222222] hover:bg-[#F8F8F8] hover:text-[#A85C34] rounded-2xl transition-colors"
                              data-testid={`mobile-nav-category-${cat.id}`}
                            >
                              {cat.name}
                            </Link>
                          );
                        }

                        return (
                          <div key={cat.id}>
                            <button
                              type="button"
                              onClick={() => setMobileExpanded((cur) => (cur === menuKey ? null : menuKey))}
                              className="w-full flex items-center justify-between px-4 py-4 text-base font-medium text-[#222222] hover:bg-[#F8F8F8] hover:text-[#A85C34] rounded-2xl transition-colors"
                              data-testid={`mobile-nav-category-${cat.id}`}
                            >
                              {cat.name}
                              <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                            </button>
                            {isExpanded && (
                              <div className="pl-4 pb-2 flex flex-col gap-0.5">
                                <Link
                                  to={`/kategori/${cat.id}`}
                                  onClick={closeAll}
                                  className="px-4 py-3 text-sm font-semibold text-[#A85C34] hover:bg-[#F8F8F8] rounded-xl transition-colors"
                                >
                                  Tümünü Gör
                                </Link>
                                {children.map((child) => (
                                  <Link
                                    key={child.id}
                                    to={`/kategori/${child.id}`}
                                    onClick={closeAll}
                                    className="px-4 py-3 text-sm text-[#555555] hover:bg-[#F8F8F8] hover:text-[#A85C34] rounded-xl transition-colors"
                                  >
                                    {child.name}
                                  </Link>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {navigation.after.map((item) => {
                        if (!item.type) {
                          return (
                            <Link
                              key={item.label}
                              to={item.to}
                              onClick={closeAll}
                              className="block px-4 py-4 text-base font-medium text-[#222222] hover:bg-[#F8F8F8] hover:text-[#A85C34] rounded-2xl transition-colors"
                              data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                              {item.label}
                            </Link>
                          );
                        }

                        const isExpanded = mobileExpanded === item.label;
                        return (
                          <div key={item.label}>
                            <button
                              type="button"
                              onClick={() => setMobileExpanded((cur) => (cur === item.label ? null : item.label))}
                              className="w-full flex items-center justify-between px-4 py-4 text-base font-medium text-[#222222] hover:bg-[#F8F8F8] hover:text-[#A85C34] rounded-2xl transition-colors"
                              data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                              {item.label}
                              <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                            </button>
                            {isExpanded && (
                              <div className="pl-4 pb-2 flex flex-col gap-0.5">
                                {emotions.map((e) => (
                                  <Link
                                    key={e.id}
                                    to={`/urunler?vesile=${e.id}`}
                                    onClick={closeAll}
                                    className="px-4 py-3 text-sm text-[#555555] hover:bg-[#F8F8F8] hover:text-[#A85C34] rounded-xl transition-colors"
                                  >
                                    {e.label}
                                  </Link>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {isLoggedIn ? (
                        <>
                          <Link
                            to="/hesabim/siparislerim"
                            onClick={closeAll}
                            className="flex items-center gap-3 px-4 py-4 text-base font-medium text-[#222222] hover:bg-[#F8F8F8] hover:text-[#A85C34] rounded-2xl transition-colors"
                            data-testid="mobile-account-orders-link"
                          >
                            <Package className="w-4 h-4 text-[#A85C34]" />
                            Siparişlerim
                          </Link>
                          <button
                            onClick={() => { logout(); closeAll(); }}
                            className="w-full flex items-center gap-3 px-4 py-4 text-base font-medium text-[#888888] hover:bg-[#F8F8F8] hover:text-[#222222] rounded-2xl transition-colors text-left"
                            data-testid="mobile-account-logout"
                          >
                            <LogOut className="w-4 h-4" />
                            Çıkış Yap
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => { closeAll(); setAuthOpen(true); }}
                          className="w-full flex items-center gap-3 px-4 py-4 text-base font-medium text-[#222222] hover:bg-[#F8F8F8] hover:text-[#A85C34] rounded-2xl transition-colors text-left"
                          data-testid="mobile-account-login"
                        >
                          <User className="w-4 h-4 text-[#A85C34]" />
                          Giriş Yap / Kayıt Ol
                        </button>
                      )}
                    </nav>
                    <div className="p-6 border-t border-[#EAEAEA] space-y-3">
                      <a
                        href={`tel:${business.phoneRaw}`}
                        className="flex items-center gap-3 px-4 py-3 bg-[#A85C34] text-white rounded-full font-medium"
                        style={{ backgroundColor: "var(--frida-primary)" }}
                      >
                        <Phone className="w-4 h-4" />
                        {business.phone}
                      </a>
                      <p className="text-xs text-[#888888] text-center">{business.hours}</p>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>

        {/* Search overlay */}
        {searchOpen && (
          <div className="absolute inset-x-0 top-0 bg-white border-b border-[#EAEAEA] shadow-lg z-50" data-testid="search-overlay">
            <form
              className="frida-container py-6 flex items-center gap-4"
              onSubmit={(e) => {
                e.preventDefault();
                const q = searchQuery.trim();
                setSearchOpen(false);
                navigate(q ? `/urunler?q=${encodeURIComponent(q)}` : "/urunler");
              }}
            >
              <Search className="w-5 h-5 text-[#888888]" />
              <input
                autoFocus
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Çiçek, buket, kategori ara..."
                className="flex-1 text-lg outline-none bg-transparent placeholder:text-[#888888]"
                data-testid="search-input"
              />
              <button
                type="button"
                onClick={() => setSearchOpen(false)}
                aria-label="Kapat"
                data-testid="search-close"
                className="w-10 h-10 rounded-full hover:bg-[#F8F8F8] flex items-center justify-center"
              >
                <X className="w-5 h-5" />
              </button>
            </form>
          </div>
        )}
      </header>

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} onSuccess={() => setAuthOpen(false)} />
    </>
  );
}
